import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';

interface UsageData {
  tokensUsed: number;
  tokensCap: number | null;
  percentUsed: number;
  tier: string;
  resetsAt: string | null;
  isExhausted: boolean;
  isWarning: boolean;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function UsageMeter() {
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    apiFetch<UsageData>('/api/usage/tokens')
      .then(setUsage)
      .catch(() => {});
  }, []);

  // Only show for capped tiers
  if (!usage || usage.tokensCap === null) return null;

  const barColor = usage.isExhausted
    ? '#ef4444'
    : usage.isWarning
      ? '#f59e0b'
      : '#22c55e';

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={() => setShowPopover(!showPopover)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '8px 12px',
          borderRadius: 'var(--radius-sm)',
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
          fontFamily: 'var(--font-body)',
          transition: 'background 150ms, color 150ms',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 500 }}>
          {formatTokens(usage.tokensUsed)} / {formatTokens(usage.tokensCap)} tokens
        </span>
        <div
          style={{
            width: '100%',
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.12)',
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="usage-bar"
            style={{
              width: `${Math.min(usage.percentUsed, 100)}%`,
              height: '100%',
              borderRadius: 2,
              backgroundColor: barColor,
              transition: 'width 300ms ease',
            }}
          />
        </div>
      </button>

      {showPopover && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 8,
            padding: 16,
            borderRadius: 12,
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            width: 260,
            zIndex: 100,
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            Token allowance
          </p>
          <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--text-muted)' }}>
            {usage.tokensUsed.toLocaleString()} / {usage.tokensCap.toLocaleString()} used this month
          </p>
          {usage.resetsAt && (
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted)' }}>
              Resets on {formatDate(usage.resetsAt)}
            </p>
          )}
          <a
            href="/upgrade"
            style={{
              display: 'inline-block',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--primary-light)',
              textDecoration: 'none',
            }}
          >
            Upgrade to Starter for 5M tokens &rarr;
          </a>
        </div>
      )}
    </div>
  );
}
